import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * DemoModeContext — exposes whether the app is running in "demo" mode.
 *
 * Demo mode is activated by visiting the app with ``?demo=true`` in the URL
 * (case-insensitive) and then persists to the current tab's sessionStorage
 * so clicking internal links doesn't drop out of demo mode.
 *
 * In demo mode:
 *   - Auth is bypassed; pages use RoleContext + seed data.
 *   - The sidebar RoleSwitcher is visible.
 *
 * Without demo mode:
 *   - Routes are gated by AuthContext.
 *   - RoleSwitcher is hidden; role comes from the JWT.
 */

const DemoModeContext = createContext(false);
const STORAGE_KEY = 'atlacis_demo_mode';

function initialDemoMode() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = (params.get('demo') || '').toLowerCase() === 'true';
    if (fromQuery) {
      window.sessionStorage.setItem(STORAGE_KEY, 'true');
      return true;
    }
    return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function DemoModeProvider({ children }) {
  const [isDemo, setIsDemo] = useState(initialDemoMode);

  // Re-check on URL changes in case the user toggles demo mid-session.
  useEffect(() => {
    const onPop = () => setIsDemo(initialDemoMode());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const value = useMemo(() => isDemo, [isDemo]);
  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
}

export function useDemoMode() {
  return useContext(DemoModeContext);
}
